import { Controller } from "@hotwired/stimulus"

/**
 * Les suggestions de l'annuaire — carte #1595, lot D. `GE-04`, §8.5.
 *
 * 🔴 **Il SUGGÈRE, il n'impose jamais.** §4.3 fait de la saisie libre le chemin nominal :
 * l'annuaire est vide au premier jour, et « école introuvable » y serait vrai pour toutes les
 * écoles. Le champ reste donc entièrement libre — choisir une suggestion ne fait que recopier
 * son libellé, ce qui suffit à rejoindre la ligne existante côté serveur.
 *
 * ⚠️ **Une liste vide n'est jamais annoncée.** Un « aucun résultat » transformerait un champ
 * facultatif en épreuve, sur le champ qui fait déjà le plus abandonner ce formulaire.
 *
 * ⚠️ **La borne des 3 caractères est SERVEUR** (`SchoolMatcher::MIN_QUERY`) : celle d'ici ne
 * fait qu'éviter des requêtes inutiles. Deux bornes, une seule règle — et c'est le serveur qui
 * la porte, parce qu'un client se contourne.
 */
export default class extends Controller {
  static targets = ["input", "list"]
  static values = { url: String, delay: { type: Number, default: 250 } }

  connect() {
    this.timeout = null
    this.controller = null
  }

  disconnect() {
    clearTimeout(this.timeout)
    this.controller?.abort()
  }

  saisir() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.chercher(), this.delayValue)
  }

  async chercher() {
    const requete = this.inputTarget.value.trim()
    if (requete.length < 3) return this.vider()

    // ⚠️ **La requête précédente est ANNULÉE** : sans cela, une réponse lente arrivée après
    // une rapide réécrit la liste avec les suggestions d'un mot déjà effacé.
    this.controller?.abort()
    this.controller = new AbortController()

    try {
      // The URL may already carry a query (the chosen `country_code`): append `q` to it
      // rather than concatenating a second `?`.
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", requete)
      const reponse = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.controller.signal,
      })
      if (!reponse.ok) return this.vider()
      this.rendre((await reponse.json()).data || [])
    } catch (erreur) {
      // 🔴 **Une panne de suggestion n'est PAS une panne de formulaire.** Le champ reste
      // saisissable, et le parent ne saura même pas qu'il y a eu une requête.
      if (erreur.name !== "AbortError") this.vider()
    }
  }

  rendre(suggestions) {
    this.vider()
    suggestions.forEach((ecole) => {
      const bouton = document.createElement("button")
      bouton.type = "button"
      bouton.textContent = ecole.label
      bouton.dataset.testid = "directory-suggestion"
      bouton.className = "g4l-suggestion"
      bouton.addEventListener("click", () => {
        this.inputTarget.value = ecole.label
        this.vider()
      })
      this.listTarget.appendChild(bouton)
    })
  }

  vider() {
    this.listTarget.replaceChildren()
  }
}
